import React, { useState, useEffect, useMemo } from 'react';
import { withStyles } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Header from '~/pages/Maintenance/Header';
import PF2MTable from '~/components/PF2MTable';
import {
  getSystems,
  getFailureModeEquipmentGroupMaintenanceItemsTemplate,
  importFailureModeEquipmentGroupMaintenanceItems,
  exportFailureModeEquipmentGroupMaintenanceItems,
  getFailureModes,
  getSystemItems,
  getFailureModesSystemItems,
  getFailureModeEquipmentGroupMaintenanceItems,
} from '~/store/maintenance/actions';
import { getEquipmentsGroups, getEquipmentTypes } from '~/store/manager/actions';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MSearchInput from '~/components/PF2MSearchInput';
import styles from '../styles';

const RegisterTab = ({
  classes,
}) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const systems = useSelector(state => state.maintenance.systems);
  const [selecteds, setSelecteds] = useState({
    equipmentType: 0,
    equipmentGroup: 0,
    system: 0,
    has: false,
  });

  const filtersReady = selecteds.equipmentGroup > 0 && selecteds.system > 0;

  const setEquipType = (e) => {
    setSelecteds(st => ({
      ...st,
      equipmentType: e.target.value,
      equipmentGroup: 0,
      system: 0,
      has: false,
    }));
    setSearchText('');
  };

  const setEquipGroup = (e) => {
    setSelecteds(st => ({
      ...st,
      equipmentGroup: e.target.value,
      system: 0,
      has: false,
    }));
    setSearchText('');
  };

  const setSystem = (e) => {
    const system = e.target.value;
    setSelecteds(st => ({
      ...st,
      system,
      has: !!(system && st.equipmentGroup),
    }));
    setSearchText('');
  };

  const failureModeEquipmentGroup = useSelector(
    s => s.maintenance.failureModeEquipmentGroupFiltered,
  );

  const activeRows = useMemo(() => (
    failureModeEquipmentGroup.filter(item => item.active)
  ), [failureModeEquipmentGroup]);

  const filteredData = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return activeRows;
    return activeRows.filter(item => (
      item.system_item_maintenance_name?.toLowerCase().includes(term)
      || item.failure_mode_maintenance_name?.toLowerCase().includes(term)
    ));
  }, [activeRows, searchText]);

  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (!dataLoaded) {
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentsGroups());
      dispatch(getSystems());
      dispatch(getSystemItems());
      dispatch(getFailureModes());
      dispatch(getFailureModesSystemItems());

      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  useEffect(() => {
    if (selecteds.equipmentGroup && selecteds.system) {
      dispatch(getFailureModeEquipmentGroupMaintenanceItems(selecteds));
    }
  }, [selecteds.equipmentGroup, selecteds.system, selecteds, dispatch]);

  const columns = [
    {
      field: 'equipment_group_name',
      title: translate('common:EquipmentGroup'),
    },
    {
      field: 'system_maintenance_name',
      title: translate('common:System'),
    },
    {
      field: 'system_item_maintenance_name',
      title: translate('common:SystemItems'),
    },
    {
      field: 'failure_mode_maintenance_name',
      title: translate('common:FailureModes'),
    },
  ];

  return (
    <div className={classes.tabContainer}>
      <div className={classes.filtersHeaderContainer}>
        <div className={classes.headerActionsContainer}>
          <Header
            getTemplateAction={
              () => dispatch(getFailureModeEquipmentGroupMaintenanceItemsTemplate())
            }
            importAction={
              file => dispatch(importFailureModeEquipmentGroupMaintenanceItems(file, selecteds))
            }
            exportAction={filtersReady
              ? (() => dispatch(exportFailureModeEquipmentGroupMaintenanceItems(selecteds)))
              : undefined
            }
          />
        </div>
        <div className={classes.selectorsContainer}>
          <div style={{ marginRight: 10 }}>
            <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
              <p className={classes.formLabel}>
                {`${translate('common:FilterByEquipmentType')}:`}
              </p>
              <PF2MSearchSelectOutlined
                value={selecteds.equipmentType}
                onChange={setEquipType}
                fullWidth={false}
                className={classes.typeSelector}
              >
                {[{ value: 0, label: translate('common:Select') }]
                  .concat(...equipmentTypes
                    .map(e => ({ value: e.id, label: e.name })))}
              </PF2MSearchSelectOutlined>
            </div>
          </div>
          <div style={{ marginRight: 10 }}>
            <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
              <p className={classes.formLabel}>
                {`${translate('common:FilterByEquipmentGroup')}:`}
              </p>
              <PF2MSearchSelectOutlined
                value={selecteds.equipmentGroup}
                onChange={setEquipGroup}
                fullWidth={false}
                className={classes.typeSelector}
              >
                {[{ value: 0, label: translate('common:Select') }]
                  .concat(...equipmentsGroups
                    .filter(e => selecteds.equipmentType === 0
                      || e.id_equipament === selecteds.equipmentType)
                    .map(e => ({ value: e.id, label: e.name })))}
              </PF2MSearchSelectOutlined>
            </div>
          </div>
          <div style={{ marginRight: 10 }}>
            <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
              <p className={classes.formLabel}>
                {`${translate('common:FilterSystem')}:`}
              </p>
              <PF2MSearchSelectOutlined
                value={selecteds.system}
                onChange={setSystem}
                fullWidth={false}
                className={classes.typeSelector}
              >
                {[{ value: 0, label: translate('common:Select') }]
                  .concat(...systems
                    .map(e => ({ value: e.id, label: e.name })))}
              </PF2MSearchSelectOutlined>
            </div>
          </div>
          {filtersReady && failureModeEquipmentGroup.length > 0 && (
            <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
              <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
                <PF2MSearchInput
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder={translate('common:Find')}
                  startIcon={<SearchIcon fontSize="small" color="primary" />}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {filtersReady && (
        <PF2MTable
          className={classes.table}
          data={filteredData}
          columns={columns}
          keyExtractor={item => `${item.id_failure_mode_system_item}-${item.equipment_group_id}`}
        />
      )}
    </div>
  );
};

RegisterTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

RegisterTab.defaultProps = {};

export default withStyles(styles)(RegisterTab);
